import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Nav from "./components/Nav";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import Films from "./pages/Films";
import FilmDetail from "./pages/FilmDetail";
import About from "./pages/About";
import Team from "./pages/Team";
import Gallery from "./pages/Gallery";
import News from "./pages/News";
import Contact from "./pages/Contact";
import InProduction from "./pages/InProduction";
import OnTheFloor from "./pages/OnTheFloor";
import NotFound from "./pages/NotFound";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import useLenis from "./hooks/useLenis";
import { findFilm } from "./data/films";

const BRAND = "Dhara Kala Movies";
const SITE_URL = "https://dharakalamovies.com";
const DEFAULT_OG_IMAGE = "https://customer-assets-agu9un31.emergentagent.net/job_dhara-kala-films/artifacts/fsy0hxgs_DharaKala.png";
const DEFAULT_DESC = "Dhara Kala Movies — a Kathmandu-based film production company crafting documentaries and cultural films for private events, festivals and social awareness. Founded 2019.";

const PAGE_META = {
  "/": { title: "Home", desc: "Stories rooted in culture. Cinema made for the world. Documentaries, cultural films and feature slate from Dhara Kala Movies, Kathmandu — founded 2019." },
  "/about": { title: "About", desc: "Founded in 2019 in New Baneshwor, Kathmandu, Dhara Kala Movies produces documentaries and cultural films for private events, festivals and social awareness." },
  "/films": { title: "Projects", desc: "The full Dhara Kala Movies slate — 6 completed documentaries, 7 developed features, 3 in production, 2 on the floor, and 2 concept projects." },
  "/in-production": { title: "In Production", desc: "Three Dhara Kala Movies features currently in production — Valley of the Living Gods, The Silent Monastery, and Fire Beneath the Snow." },
  "/on-the-floor": { title: "On the Floor", desc: "Now filming: Pilgrimage to the Hidden Lake and The Mask of Bhairav — dispatches from the Dhara Kala Movies set." },
  "/team": { title: "Team", desc: "Forty-plus Nepali and Indian film professionals across direction, production, camera, art, sound, post and festivals — the Dhara Kala Movies studio team." },
  "/gallery": { title: "Trailers & Gallery", desc: "Trailer placeholders, production stills, behind-the-scenes and cultural-research imagery from the Dhara Kala Movies slate." },
  "/news": { title: "News & Festivals", desc: "News, screenings and festival participation from Dhara Kala Movies — including TIFF: The Market 2026 market-participation objectives." },
  "/contact": { title: "Contact", desc: "Reach Dhara Kala Movies for festivals, distribution, co-production, media, casting and general inquiries — dharakala@outlook.com." },
  "/privacy": { title: "Privacy Policy", desc: "Privacy policy for dharakalamovies.com — how we handle information submitted through our inquiry and newsletter forms." },
  "/terms": { title: "Terms & Conditions", desc: "Terms and conditions of use for dharakalamovies.com." },
};

function setMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(id, data) {
  let el = document.head.querySelector(`script[data-jsonld="${id}"]`);
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-jsonld", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function SEOHead() {
  const { pathname } = useLocation();
  useEffect(() => {
    let pageTitle = "";
    let description = DEFAULT_DESC;
    let ogImage = DEFAULT_OG_IMAGE;
    let ogType = "website";
    let jsonLd = null;

    if (PAGE_META[pathname]) {
      pageTitle = PAGE_META[pathname].title;
      description = PAGE_META[pathname].desc;
    } else if (pathname.startsWith("/films/")) {
      const slug = pathname.replace("/films/", "");
      const film = findFilm(slug);
      if (film) {
        pageTitle = film.title;
        description = film.shortSynopsis || DEFAULT_DESC;
        ogImage = film.poster || DEFAULT_OG_IMAGE;
        ogType = "video.movie";
        jsonLd = {
          "@context": "https://schema.org",
          "@type": "Movie",
          name: film.title,
          alternateName: [film.nepaliTitle, film.hindiTitle].filter(Boolean),
          description: film.synopsis || film.shortSynopsis,
          genre: film.genre,
          image: film.poster,
          inLanguage: film.language,
          datePublished: film.year,
          director: film.director ? { "@type": "Person", name: film.director } : undefined,
          author: film.writer ? { "@type": "Person", name: film.writer } : undefined,
          productionCompany: { "@type": "Organization", name: BRAND, url: SITE_URL },
        };
      } else {
        pageTitle = "Projects";
      }
    } else {
      pageTitle = "Not Found";
      description = "The page you're looking for isn't on our reel. Return to Dhara Kala Movies homepage.";
    }

    const fullTitle = `${pageTitle} | ${BRAND}`;
    const canonical = `${SITE_URL}${pathname}`;

    document.title = fullTitle;
    setMeta("description", description);
    setLink("canonical", canonical);
    setMeta("robots", "index, follow");
    setMeta("og:title", fullTitle, "property");
    setMeta("og:description", description, "property");
    setMeta("og:url", canonical, "property");
    setMeta("og:type", ogType, "property");
    setMeta("og:image", ogImage, "property");
    setMeta("og:site_name", BRAND, "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", fullTitle);
    setMeta("twitter:description", description);
    setMeta("twitter:image", ogImage);

    // Base Organization JSON-LD, always present
    setJsonLd("organization", {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: BRAND,
      alternateName: "धाराकला",
      url: SITE_URL,
      logo: DEFAULT_OG_IMAGE,
      foundingDate: "2019",
      address: {
        "@type": "PostalAddress",
        streetAddress: "New Baneshwor",
        addressLocality: "Kathmandu",
        addressCountry: "Nepal",
      },
      email: "dharakala@outlook.com",
    });

    // WebSite JSON-LD
    setJsonLd("website", {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: BRAND,
      url: SITE_URL,
    });

    // Per-page Movie JSON-LD
    if (jsonLd) setJsonLd("primary", jsonLd);
    else {
      // clear any previous primary schema
      const prev = document.head.querySelector('script[data-jsonld="primary"]');
      if (prev) prev.remove();
    }
  }, [pathname]);
  return null;
}

function AppShell() {
  useLenis();
  return (
    <>
      <ScrollToTop />
      <SEOHead />
      <Nav />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/films" element={<Films />} />
        <Route path="/films/:slug" element={<FilmDetail />} />
        <Route path="/in-production" element={<InProduction />} />
        <Route path="/on-the-floor" element={<OnTheFloor />} />
        <Route path="/about" element={<About />} />
        <Route path="/team" element={<Team />} />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/news" element={<News />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      <Footer />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
    </div>
  );
}

export default App;
